import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { devLog } from '../utils/devLog';
import EmailList from './email/EmailList';
import SchedulerPanel from './email/SchedulerPanel';
import SmtpConfigPanel from './email/SmtpConfigPanel';

// Synthetic "plant" entry that drives the Consolidated (All Plants) email config.
const CONSOLIDATED_ID = '__consolidated__';
const CONSOLIDATED_OPTION = { id: CONSOLIDATED_ID, name: 'Consolidated (All Plants)', location: '' };

const EmailConfigView = ({ plants, selectedPlant, setSelectedPlant, token }) => {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerHour, setSchedulerHour] = useState(6);
  const [smtpConfig, setSmtpConfig] = useState({ smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', smtp_from_email: '', use_tls: true });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [showSmtpForm, setShowSmtpForm] = useState(false);

  const isConsolidated = selectedPlant?.id === CONSOLIDATED_ID;
  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadSchedulerStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-scheduler/status`, { headers: authHeaders });
      setSchedulerStatus(res.data);
    } catch (err) { devLog.error('Failed to load scheduler status:', err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadEmails = useCallback(async () => {
    try {
      setLoading(true);
      const url = isConsolidated
        ? `${API}/email-scheduler/consolidated-recipients`
        : `${API}/plants/${selectedPlant.id}/emails`;
      const res = await axios.get(url, { headers: authHeaders });
      setEmails(res.data.emails || []);
    } catch (err) { devLog.error('Failed to load emails:', err); setEmails([]); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlant, token, isConsolidated]);

  const loadSmtpConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/smtp-config`, { headers: authHeaders });
      setSmtpConfig(prev => ({ ...prev, smtp_host: res.data.smtp_host || '', smtp_port: res.data.smtp_port || 587,
        smtp_user: res.data.smtp_user || '', smtp_from_email: res.data.smtp_from_email || '', use_tls: res.data.use_tls !== false }));
    } catch (err) { devLog.error('Failed to load SMTP config:', err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (selectedPlant) loadEmails(); loadSchedulerStatus(); }, [selectedPlant, loadEmails, loadSchedulerStatus]);
  useEffect(() => { loadSmtpConfig(); }, [loadSmtpConfig]);

  const handleEnableScheduler = async () => {
    if (!selectedPlant) { alert('Please select a plant first'); return; }
    const target = isConsolidated ? 'consolidated' : selectedPlant.id;
    try {
      const res = await axios.post(
        `${API}/email-scheduler/enable?target=${encodeURIComponent(target)}&hour=${schedulerHour}&minute=0`,
        {}, { headers: authHeaders });
      alert(res.data.message); loadSchedulerStatus();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); }
  };

  const handleDisableScheduler = async () => {
    if (!selectedPlant) { alert('Please select a plant first'); return; }
    const target = isConsolidated ? 'consolidated' : selectedPlant.id;
    try {
      const res = await axios.post(
        `${API}/email-scheduler/disable?target=${encodeURIComponent(target)}`,
        {}, { headers: authHeaders });
      alert(res.data.message); loadSchedulerStatus();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); }
  };

  const handleTriggerNow = async () => {
    if (!window.confirm('Send today\'s auto-email now to all currently-enabled targets? This uses the same targets as the scheduled daily run.')) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/email-scheduler/trigger-now`, {}, { headers: authHeaders });
      alert(res.data.message + (res.data.last_run ? `\nLast run: ${res.data.last_run}` : ''));
      loadSchedulerStatus();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Failed'));
    } finally { setLoading(false); }
  };

  const handleTestEmail = async () => {
    if (!selectedPlant) { alert('Please select a plant first'); return; }
    try {
      setLoading(true);
      const url = isConsolidated
        ? `${API}/email-scheduler/test/consolidated`
        : `${API}/email-scheduler/test/${selectedPlant.id}`;
      const res = await axios.post(url, {}, { headers: authHeaders });
      alert(res.data.message);
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); } finally { setLoading(false); }
  };

  const handleSaveSmtp = async () => {
    try { setSmtpSaving(true); await axios.post(`${API}/smtp-config`, smtpConfig, { headers: authHeaders });
      alert('SMTP configuration saved!'); setShowSmtpForm(false); loadSchedulerStatus();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); } finally { setSmtpSaving(false); }
  };

  const handleTestSmtp = async () => {
    try { setSmtpSaving(true);
      const res = await axios.post(`${API}/smtp-config/test?test_email=${smtpConfig.smtp_from_email || smtpConfig.smtp_user}`, {}, { headers: authHeaders });
      alert(res.data.message);
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); } finally { setSmtpSaving(false); }
  };

  const persistEmails = async (updated) => {
    if (isConsolidated) {
      await axios.put(`${API}/email-scheduler/consolidated-recipients`, updated, { headers: authHeaders });
    } else {
      await axios.put(`${API}/plants/${selectedPlant.id}/emails`, updated, { headers: authHeaders });
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) { alert('Please enter a valid email'); return; }
    if (emails.includes(newEmail)) { alert('Email already exists'); return; }
    try { setLoading(true); const updated = [...emails, newEmail];
      await persistEmails(updated);
      setEmails(updated); setNewEmail(''); alert('Email added!');
      loadSchedulerStatus();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); } finally { setLoading(false); }
  };

  const handleRemoveEmail = async (emailToRemove) => {
    if (!window.confirm(`Remove ${emailToRemove}?`)) return;
    try { setLoading(true); const updated = emails.filter(e => e !== emailToRemove);
      await persistEmails(updated);
      setEmails(updated); alert('Email removed!');
      loadSchedulerStatus();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); } finally { setLoading(false); }
  };

  const dropdownOptions = [CONSOLIDATED_OPTION, ...plants];

  const handleSelectChange = (e) => {
    const id = e.target.value;
    if (id === CONSOLIDATED_ID) { setSelectedPlant(CONSOLIDATED_OPTION); return; }
    setSelectedPlant(plants.find(p => p.id === id));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Select Plant for Email Configuration</label>
        <select
          value={selectedPlant?.id || ''}
          onChange={handleSelectChange}
          className="w-full px-4 py-3 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA] text-base font-medium"
          data-testid="email-plant-select"
        >
          {dropdownOptions.map(opt => (
            <option key={opt.id} value={opt.id} data-testid={`email-plant-option-${opt.id}`}>
              {opt.name}{opt.location ? ` (${opt.location})` : ''}
            </option>
          ))}
        </select>
        {isConsolidated && (
          <p className="mt-3 text-sm text-[#156ACA] bg-blue-50 border border-blue-200 rounded-md px-3 py-2" data-testid="consolidated-info">
            Consolidated (All Plants) — recipients here receive a single rolled-up MTD report covering every active plant
            (KPIs, per-plant summary, utilization, volumetric efficiency, cost breakdown, and stoppages).
          </p>
        )}
      </div>

      <EmailList
        emails={emails}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        loading={loading}
        selectedPlant={selectedPlant}
        onAddEmail={handleAddEmail}
        onRemoveEmail={handleRemoveEmail}
      />

      <SchedulerPanel
        schedulerStatus={schedulerStatus}
        schedulerHour={schedulerHour}
        setSchedulerHour={setSchedulerHour}
        loading={loading}
        emails={emails}
        selectedPlant={selectedPlant}
        onEnable={handleEnableScheduler}
        onDisable={handleDisableScheduler}
        onTestEmail={handleTestEmail}
        onTriggerNow={handleTriggerNow}
      />

      <SmtpConfigPanel
        smtpConfig={smtpConfig}
        setSmtpConfig={setSmtpConfig}
        smtpSaving={smtpSaving}
        schedulerStatus={schedulerStatus}
        showSmtpForm={showSmtpForm}
        setShowSmtpForm={setShowSmtpForm}
        onSave={handleSaveSmtp}
        onTest={handleTestSmtp}
      />
    </div>
  );
};

export default EmailConfigView;
